const clamp01 = (value) => Math.max(0, Math.min(1, value));
const EPSILON = 216 / 24389;
const KAPPA = 24389 / 27;
const D50 = { x: 0.34567, y: 0.3585 };
const D60 = { x: 0.32168, y: 0.33767 };
const D65 = { x: 0.3127, y: 0.329 };
const BRADFORD = [
    [0.8951, 0.2664, -0.1614],
    [-0.7502, 1.7135, 0.0367],
    [0.0389, -0.0685, 1.0296],
];
const BRADFORD_INVERSE = [
    [0.9869929, -0.1470543, 0.1599627],
    [0.4323053, 0.5183603, 0.0492912],
    [-0.0085287, 0.0400428, 0.9684867],
];
const CAMERA_PROFILE_WARNING = 'Camera profile registered but exact log formula is not implemented yet.';
const round = (value, digits = 10) => Number(value.toFixed(digits));
function multiplyMatrixVector(matrix, vector) {
    return [
        matrix[0][0] * vector[0] + matrix[0][1] * vector[1] + matrix[0][2] * vector[2],
        matrix[1][0] * vector[0] + matrix[1][1] * vector[1] + matrix[1][2] * vector[2],
        matrix[2][0] * vector[0] + matrix[2][1] * vector[1] + matrix[2][2] * vector[2],
    ];
}
function multiplyMatrices(a, b) {
    return [
        [
            a[0][0] * b[0][0] + a[0][1] * b[1][0] + a[0][2] * b[2][0],
            a[0][0] * b[0][1] + a[0][1] * b[1][1] + a[0][2] * b[2][1],
            a[0][0] * b[0][2] + a[0][1] * b[1][2] + a[0][2] * b[2][2],
        ],
        [
            a[1][0] * b[0][0] + a[1][1] * b[1][0] + a[1][2] * b[2][0],
            a[1][0] * b[0][1] + a[1][1] * b[1][1] + a[1][2] * b[2][1],
            a[1][0] * b[0][2] + a[1][1] * b[1][2] + a[1][2] * b[2][2],
        ],
        [
            a[2][0] * b[0][0] + a[2][1] * b[1][0] + a[2][2] * b[2][0],
            a[2][0] * b[0][1] + a[2][1] * b[1][1] + a[2][2] * b[2][1],
            a[2][0] * b[0][2] + a[2][1] * b[1][2] + a[2][2] * b[2][2],
        ],
    ];
}
function invertMatrix3(matrix) {
    const [[a, b, c], [d, e, f], [g, h, i]] = matrix;
    const A = e * i - f * h;
    const B = -(d * i - f * g);
    const C = d * h - e * g;
    const D = -(b * i - c * h);
    const E = a * i - c * g;
    const F = -(a * h - b * g);
    const G = b * f - c * e;
    const H = -(a * f - c * d);
    const I = a * e - b * d;
    const determinant = a * A + b * B + c * C;
    if (Math.abs(determinant) < 1e-12)
        throw new Error('Cannot invert singular 3x3 matrix.');
    return [
        [A / determinant, D / determinant, G / determinant],
        [B / determinant, E / determinant, H / determinant],
        [C / determinant, F / determinant, I / determinant],
    ];
}
function xyToXYZ(whitePoint) {
    return {
        x: whitePoint.x / whitePoint.y,
        y: 1,
        z: (1 - whitePoint.x - whitePoint.y) / whitePoint.y,
    };
}
function matrixFromPrimaries(primaries, whitePoint) {
    const r = xyToXYZ(primaries.red);
    const g = xyToXYZ(primaries.green);
    const b = xyToXYZ(primaries.blue);
    const matrix = [
        [r.x, g.x, b.x],
        [r.y, g.y, b.y],
        [r.z, g.z, b.z],
    ];
    const white = xyToXYZ(whitePoint);
    const scale = multiplyMatrixVector(invertMatrix3(matrix), [white.x, white.y, white.z]);
    return [
        [matrix[0][0] * scale[0], matrix[0][1] * scale[1], matrix[0][2] * scale[2]],
        [matrix[1][0] * scale[0], matrix[1][1] * scale[1], matrix[1][2] * scale[2]],
        [matrix[2][0] * scale[0], matrix[2][1] * scale[1], matrix[2][2] * scale[2]],
    ];
}
function defineColorSpace(id, name, primaries, whitePoint, transferFunction) {
    const rgbToXYZMatrix = matrixFromPrimaries(primaries, whitePoint);
    return {
        id,
        name,
        primaries,
        whitePoint,
        transferFunction,
        rgbToXYZMatrix,
        xyzToRgbMatrix: invertMatrix3(rgbToXYZMatrix),
    };
}
const SRGB_PRIMARIES = {
    red: { x: 0.64, y: 0.33 },
    green: { x: 0.3, y: 0.6 },
    blue: { x: 0.15, y: 0.06 },
};
export const COLOR_SPACES = {
    sRGB: defineColorSpace('sRGB', 'sRGB', SRGB_PRIMARIES, D65, 'sRGB'),
    Rec709: defineColorSpace('Rec709', 'Rec.709', SRGB_PRIMARIES, D65, 'Rec709'),
    DisplayP3: defineColorSpace('DisplayP3', 'Display P3', {
        red: { x: 0.68, y: 0.32 },
        green: { x: 0.265, y: 0.69 },
        blue: { x: 0.15, y: 0.06 },
    }, D65, 'sRGB'),
    Rec2020: defineColorSpace('Rec2020', 'Rec.2020', {
        red: { x: 0.708, y: 0.292 },
        green: { x: 0.17, y: 0.797 },
        blue: { x: 0.131, y: 0.046 },
    }, D65, 'Gamma2.4'),
    ACEScg: defineColorSpace('ACEScg', 'ACEScg / ACES AP1', {
        red: { x: 0.713, y: 0.293 },
        green: { x: 0.165, y: 0.83 },
        blue: { x: 0.128, y: 0.044 },
    }, D60, 'Linear'),
};
export const CAMERA_PROFILES = {
    'arri-logc4-awg4': {
        id: 'arri-logc4-awg4',
        name: 'ARRI LogC4 / ARRI Wide Gamut 4',
        colorSpaceHint: 'ARRI Wide Gamut 4',
        transferFunctionHint: 'LogC4',
        implemented: false,
        transformStatus: 'metadata-only',
        warning: CAMERA_PROFILE_WARNING,
    },
    'sony-slog3-sgamut3-cine': {
        id: 'sony-slog3-sgamut3-cine',
        name: 'Sony S-Log3 / S-Gamut3.Cine',
        colorSpaceHint: 'S-Gamut3.Cine',
        transferFunctionHint: 'S-Log3',
        implemented: false,
        transformStatus: 'metadata-only',
        warning: CAMERA_PROFILE_WARNING,
    },
    'red-logfilm-redwidegamutrgb': {
        id: 'red-logfilm-redwidegamutrgb',
        name: 'RED LogFilm / REDWideGamutRGB',
        colorSpaceHint: 'REDWideGamutRGB',
        transferFunctionHint: 'RED LogFilm',
        implemented: false,
        transformStatus: 'metadata-only',
        warning: CAMERA_PROFILE_WARNING,
    },
    'blackmagic-gen5': {
        id: 'blackmagic-gen5',
        name: 'Blackmagic Gen5',
        colorSpaceHint: 'Blackmagic Wide Gamut Gen5',
        transferFunctionHint: 'Blackmagic Film Gen5',
        implemented: false,
        transformStatus: 'metadata-only',
        warning: CAMERA_PROFILE_WARNING,
    },
    'canon-cinema-gamut': {
        id: 'canon-cinema-gamut',
        name: 'Canon Cinema Gamut',
        colorSpaceHint: 'Cinema Gamut',
        transferFunctionHint: 'Canon Log',
        implemented: false,
        transformStatus: 'metadata-only',
        warning: CAMERA_PROFILE_WARNING,
    },
    'dji-d-gamut': {
        id: 'dji-d-gamut',
        name: 'DJI D-Gamut',
        colorSpaceHint: 'D-Gamut',
        transferFunctionHint: 'D-Log',
        implemented: false,
        transformStatus: 'metadata-only',
        warning: CAMERA_PROFILE_WARNING,
    },
};
export function getColorSpace(id) {
    return COLOR_SPACES[id];
}
export function getCameraProfile(id) {
    const normalized = id.trim().toLowerCase();
    return CAMERA_PROFILES[normalized] ??
        Object.values(CAMERA_PROFILES).find((profile) => profile.name.toLowerCase() === normalized);
}
/** Converts one normalized sRGB channel to linear-light RGB. */
export function rgbToLinear(value) {
    const channel = clamp01(value);
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}
/** Converts one normalized linear-light RGB channel to sRGB. */
export function linearToRgb(value) {
    const channel = Math.max(0, value);
    return clamp01(channel <= 0.0031308 ? channel * 12.92 : 1.055 * channel ** (1 / 2.4) - 0.055);
}
export function rec709Eotf(value) {
    const channel = clamp01(value);
    return channel < 0.081 ? channel / 4.5 : ((channel + 0.099) / 1.099) ** (1 / 0.45);
}
export function rec709Oetf(value) {
    const channel = Math.max(0, value);
    return clamp01(channel < 0.018 ? 4.5 * channel : 1.099 * channel ** 0.45 - 0.099);
}
export function applyTransferFunction(value, transferFunction) {
    switch (transferFunction) {
        case 'sRGB':
            return linearToRgb(value);
        case 'Rec709':
            return rec709Oetf(value);
        case 'Gamma2.4':
            return clamp01(Math.max(0, value) ** (1 / 2.4));
        case 'Linear':
            return Math.max(0, value);
        default:
            throw new Error(`Unsupported transfer function: ${transferFunction}`);
    }
}
export function removeTransferFunction(value, transferFunction) {
    switch (transferFunction) {
        case 'sRGB':
            return rgbToLinear(value);
        case 'Rec709':
            return rec709Eotf(value);
        case 'Gamma2.4':
            return Math.max(0, clamp01(value)) ** 2.4;
        case 'Linear':
            return Math.max(0, value);
        default:
            throw new Error(`Unsupported transfer function: ${transferFunction}`);
    }
}
export function isSupportedTransferFunction(transferFunction) {
    return transferFunction === 'sRGB' ||
        transferFunction === 'Rec709' ||
        transferFunction === 'Gamma2.4' ||
        transferFunction === 'Linear';
}
function removeTransfer(rgb, transferFunction) {
    return {
        r: removeTransferFunction(rgb.r, transferFunction),
        g: removeTransferFunction(rgb.g, transferFunction),
        b: removeTransferFunction(rgb.b, transferFunction),
    };
}
function applyTransfer(rgb, transferFunction) {
    return {
        r: applyTransferFunction(rgb.r, transferFunction),
        g: applyTransferFunction(rgb.g, transferFunction),
        b: applyTransferFunction(rgb.b, transferFunction),
    };
}
export function rgbToXYZ(rgb, colorSpace = 'sRGB') {
    const space = typeof colorSpace === 'string' ? getColorSpace(colorSpace) : colorSpace;
    if (!space)
        throw new Error(`Unsupported color space: ${String(colorSpace)}`);
    const linear = removeTransfer(rgb, space.transferFunction);
    const [x, y, z] = multiplyMatrixVector(space.rgbToXYZMatrix, [linear.r, linear.g, linear.b]);
    return { x, y, z };
}
export function xyzToRgb(xyz, colorSpace = 'sRGB') {
    const space = typeof colorSpace === 'string' ? getColorSpace(colorSpace) : colorSpace;
    if (!space)
        throw new Error(`Unsupported color space: ${String(colorSpace)}`);
    const [r, g, b] = multiplyMatrixVector(space.xyzToRgbMatrix, [xyz.x, xyz.y, xyz.z]);
    return applyTransfer({ r, g, b }, space.transferFunction);
}
export function bradfordAdaptationMatrix(sourceWhite, targetWhite) {
    const source = xyToXYZ(sourceWhite);
    const target = xyToXYZ(targetWhite);
    const sourceCone = multiplyMatrixVector(BRADFORD, [source.x, source.y, source.z]);
    const targetCone = multiplyMatrixVector(BRADFORD, [target.x, target.y, target.z]);
    const scale = [
        [targetCone[0] / sourceCone[0], 0, 0],
        [0, targetCone[1] / sourceCone[1], 0],
        [0, 0, targetCone[2] / sourceCone[2]],
    ];
    return multiplyMatrices(BRADFORD_INVERSE, multiplyMatrices(scale, BRADFORD));
}
export function adaptWhitePoint(xyz, sourceWhite, targetWhite) {
    const matrix = bradfordAdaptationMatrix(sourceWhite, targetWhite);
    const [x, y, z] = multiplyMatrixVector(matrix, [xyz.x, xyz.y, xyz.z]);
    return { x, y, z };
}
function labF(value) {
    return value > EPSILON ? Math.cbrt(value) : (KAPPA * value + 16) / 116;
}
function labFInverse(value) {
    const cube = value ** 3;
    return cube > EPSILON ? cube : (116 * value - 16) / KAPPA;
}
export function xyzToLab(xyz, whitePoint = D50) {
    const white = xyToXYZ(whitePoint);
    const fx = labF(xyz.x / white.x);
    const fy = labF(xyz.y / white.y);
    const fz = labF(xyz.z / white.z);
    return {
        l: 116 * fy - 16,
        a: 500 * (fx - fy),
        b: 200 * (fy - fz),
    };
}
export function labToXYZ(lab, whitePoint = D50) {
    const white = xyToXYZ(whitePoint);
    const fy = (lab.l + 16) / 116;
    const fx = lab.a / 500 + fy;
    const fz = fy - lab.b / 200;
    return {
        x: labFInverse(fx) * white.x,
        y: labFInverse(fy) * white.y,
        z: labFInverse(fz) * white.z,
    };
}
export function labToLCH(lab) {
    return {
        l: lab.l,
        c: Math.hypot(lab.a, lab.b),
        h: (Math.atan2(lab.b, lab.a) * 180 / Math.PI + 360) % 360,
    };
}
export function lchToLab(lch) {
    const radians = lch.h * Math.PI / 180;
    return {
        l: lch.l,
        a: lch.c * Math.cos(radians),
        b: lch.c * Math.sin(radians),
    };
}
export function deltaE76(first, second) {
    return Math.hypot(first.l - second.l, first.a - second.a, first.b - second.b);
}
export function deltaE2000(first, second) {
    const l1 = first.l;
    const a1 = first.a;
    const b1 = first.b;
    const l2 = second.l;
    const a2 = second.a;
    const b2 = second.b;
    const c1 = Math.hypot(a1, b1);
    const c2 = Math.hypot(a2, b2);
    const cBar = (c1 + c2) / 2;
    const cBar7 = cBar ** 7;
    const g = 0.5 * (1 - Math.sqrt(cBar7 / (cBar7 + 25 ** 7)));
    const a1p = (1 + g) * a1;
    const a2p = (1 + g) * a2;
    const c1p = Math.hypot(a1p, b1);
    const c2p = Math.hypot(a2p, b2);
    const h1p = c1p === 0 ? 0 : (Math.atan2(b1, a1p) * 180 / Math.PI + 360) % 360;
    const h2p = c2p === 0 ? 0 : (Math.atan2(b2, a2p) * 180 / Math.PI + 360) % 360;
    const deltaLp = l2 - l1;
    const deltaCp = c2p - c1p;
    let deltaHp = h2p - h1p;
    if (c1p * c2p === 0)
        deltaHp = 0;
    else if (deltaHp > 180)
        deltaHp -= 360;
    else if (deltaHp < -180)
        deltaHp += 360;
    const deltaHP = 2 * Math.sqrt(c1p * c2p) * Math.sin((deltaHp / 2) * Math.PI / 180);
    const lBarP = (l1 + l2) / 2;
    const cBarP = (c1p + c2p) / 2;
    let hBarP;
    if (c1p * c2p === 0)
        hBarP = h1p + h2p;
    else if (Math.abs(h1p - h2p) <= 180)
        hBarP = (h1p + h2p) / 2;
    else
        hBarP = (h1p + h2p + (h1p + h2p < 360 ? 360 : -360)) / 2;
    const t = 1 -
        0.17 * Math.cos((hBarP - 30) * Math.PI / 180) +
        0.24 * Math.cos((2 * hBarP) * Math.PI / 180) +
        0.32 * Math.cos((3 * hBarP + 6) * Math.PI / 180) -
        0.20 * Math.cos((4 * hBarP - 63) * Math.PI / 180);
    const deltaTheta = 30 * Math.exp(-(((hBarP - 275) / 25) ** 2));
    const rc = 2 * Math.sqrt(cBarP ** 7 / (cBarP ** 7 + 25 ** 7));
    const sl = 1 + (0.015 * ((lBarP - 50) ** 2)) / Math.sqrt(20 + ((lBarP - 50) ** 2));
    const sc = 1 + 0.045 * cBarP;
    const sh = 1 + 0.015 * cBarP * t;
    const rt = -Math.sin(2 * deltaTheta * Math.PI / 180) * rc;
    const lTerm = deltaLp / sl;
    const cTerm = deltaCp / sc;
    const hTerm = deltaHP / sh;
    return Math.sqrt(lTerm ** 2 + cTerm ** 2 + hTerm ** 2 + rt * cTerm * hTerm);
}
/** Converts normalized sRGB to OKLab. */
export function rgbToOKLab(rgb) {
    const r = rgbToLinear(rgb.r);
    const g = rgbToLinear(rgb.g);
    const b = rgbToLinear(rgb.b);
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    return {
        l: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
        a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
        b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
    };
}
/** Unclipped linear sRGB. Inverse of the published 2021 OKLab matrices:
 * https://bottosson.github.io/posts/oklab/#converting-from-linear-srgb-to-oklab
 * Retaining out-of-gamut values lets callers reduce chroma rather than clip hue.
 */
export function oklabToLinearRGB(color) {
    const l = (color.l + 0.3963377774 * color.a + 0.2158037573 * color.b) ** 3;
    const m = (color.l - 0.1055613458 * color.a - 0.0638541728 * color.b) ** 3;
    const s = (color.l - 0.0894841775 * color.a - 1.291485548 * color.b) ** 3;
    return {
        r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
        g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
        b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
    };
}
export function oklabDelta(first, second) {
    return Math.hypot(first.l - second.l, first.a - second.a, first.b - second.b);
}
/** Relative luminance from normalized sRGB. */
export function luminance(rgb) {
    return 0.2126 * rgbToLinear(rgb.r) + 0.7152 * rgbToLinear(rgb.g) + 0.0722 * rgbToLinear(rgb.b);
}
/** HSV-style saturation in the range 0..1. */
export function saturation(rgb) {
    const maximum = Math.max(rgb.r, rgb.g, rgb.b);
    const minimum = Math.min(rgb.r, rgb.g, rgb.b);
    return maximum <= 0 ? 0 : clamp01((maximum - minimum) / maximum);
}
/** Hue angle in degrees, or 0 for achromatic colors. */
export function hueAngle(rgb) {
    const maximum = Math.max(rgb.r, rgb.g, rgb.b);
    const minimum = Math.min(rgb.r, rgb.g, rgb.b);
    const delta = maximum - minimum;
    if (delta < 1e-8)
        return 0;
    let hue;
    if (maximum === rgb.r)
        hue = ((rgb.g - rgb.b) / delta) % 6;
    else if (maximum === rgb.g)
        hue = (rgb.b - rgb.r) / delta + 2;
    else
        hue = (rgb.r - rgb.g) / delta + 4;
    return (hue * 60 + 360) % 360;
}
/** OKLab Euclidean distance between two normalized sRGB colors. */
export function perceptualDistance(first, second) {
    return oklabDelta(rgbToOKLab(first), rgbToOKLab(second));
}
export function convertColorSpace(options) {
    const from = getColorSpace(options.from);
    const to = getColorSpace(options.to);
    if (!from)
        throw new Error(`Unsupported source color space: ${options.from}`);
    if (!to)
        throw new Error(`Unsupported target color space: ${options.to}`);
    return createColorTransform(options).rgb(options.color);
}
export function createColorTransform(options) {
    const from = getColorSpace(options.from);
    const to = getColorSpace(options.to);
    if (!from)
        throw new Error(`Unsupported source color space: ${options.from}`);
    if (!to)
        throw new Error(`Unsupported target color space: ${options.to}`);
    const warnings = [];
    const intent = options.intent ?? 'relative-colorimetric';
    let inputTransferFunction = options.inputTransferFunction ?? from.transferFunction;
    let outputTransferFunction = options.outputTransferFunction ?? to.transferFunction;
    if (options.inputTransferFunction && options.inputTransferFunction !== from.transferFunction) {
        warnings.push(`Input transfer override ${options.inputTransferFunction} differs from ${from.id} default ${from.transferFunction}.`);
    }
    if (options.outputTransferFunction && options.outputTransferFunction !== to.transferFunction) {
        warnings.push(`Output transfer override ${options.outputTransferFunction} differs from ${to.id} default ${to.transferFunction}.`);
    }
    if (!isSupportedTransferFunction(inputTransferFunction)) {
        warnings.push(`Unsupported input transfer function: ${inputTransferFunction}. Falling back to ${from.transferFunction}.`);
        inputTransferFunction = from.transferFunction;
    }
    if (!isSupportedTransferFunction(outputTransferFunction)) {
        warnings.push(`Unsupported output transfer function: ${outputTransferFunction}. Falling back to ${to.transferFunction}.`);
        outputTransferFunction = to.transferFunction;
    }
    const adaptation = from.whitePoint.x !== to.whitePoint.x || from.whitePoint.y !== to.whitePoint.y
        ? bradfordAdaptationMatrix(from.whitePoint, to.whitePoint)
        : undefined;
    const rgbToTargetLinear = multiplyMatrices(to.xyzToRgbMatrix, adaptation ? multiplyMatrices(adaptation, from.rgbToXYZMatrix) : from.rgbToXYZMatrix);
    const shouldClamp = intent === 'perceptual';
    const transformRgb = (pixel) => {
        const linearInput = [
            removeTransferFunction(pixel.r, inputTransferFunction),
            removeTransferFunction(pixel.g, inputTransferFunction),
            removeTransferFunction(pixel.b, inputTransferFunction),
        ];
        const [linearR, linearG, linearB] = multiplyMatrixVector(rgbToTargetLinear, linearInput);
        const converted = {
            r: applyTransferFunction(linearR, outputTransferFunction),
            g: applyTransferFunction(linearG, outputTransferFunction),
            b: applyTransferFunction(linearB, outputTransferFunction),
        };
        return shouldClamp
            ? { r: clamp01(converted.r), g: clamp01(converted.g), b: clamp01(converted.b) }
            : converted;
    };
    return {
        from: from.id,
        to: to.id,
        intent,
        warnings,
        rgb: transformRgb,
        rgbArray(input) {
            const scale = input instanceof Uint8ClampedArray ? 1 / 255 : 1;
            const output = new Float32Array(input.length);
            for (let index = 0; index + 2 < input.length; index += 3) {
                const converted = transformRgb({
                    r: (input[index] ?? 0) * scale,
                    g: (input[index + 1] ?? 0) * scale,
                    b: (input[index + 2] ?? 0) * scale,
                });
                output[index] = converted.r;
                output[index + 1] = converted.g;
                output[index + 2] = converted.b;
            }
            return output;
        },
    };
}
export class CameraProfileRegistry {
    profiles = new Map(Object.entries(CAMERA_PROFILES));
    register(profile) {
        this.profiles.set(profile.id, profile);
    }
    get(id) {
        const normalized = id.trim().toLowerCase();
        return this.profiles.get(normalized) ??
            [...this.profiles.values()].find((profile) => profile.name.toLowerCase() === normalized);
    }
    list() {
        return [...this.profiles.values()];
    }
}
export const cameraProfileRegistry = new CameraProfileRegistry();
function samplingBudget(options, fallback = 50_000) {
    if (options?.maxSamples)
        return options.maxSamples;
    if (options?.quality === 'realtime')
        return 6_000;
    if (options?.quality === 'balanced')
        return 18_000;
    if (options?.quality === 'quality')
        return 50_000;
    return fallback;
}
function sampleImageData(frame, options) {
    const total = frame.width * frame.height;
    const sampling = typeof options === 'number' ? { maxSamples: options } : options;
    const target = Math.min(samplingBudget(sampling), total);
    const step = sampling.stride ?? Math.max(1, Math.floor(Math.sqrt(total / Math.max(1, target))));
    const samples = [];
    for (let y = 0; y < frame.height; y += step) {
        for (let x = 0; x < frame.width; x += step) {
            const offset = (y * frame.width + x) * 4;
            samples.push({
                x,
                y,
                color: {
                    r: (frame.data[offset] ?? 0) / 255,
                    g: (frame.data[offset + 1] ?? 0) / 255,
                    b: (frame.data[offset + 2] ?? 0) / 255,
                },
            });
            if (samples.length >= target)
                return samples;
        }
    }
    return samples;
}
export class ColorManagedPipeline {
    options;
    warnings = [];
    constructor(options) {
        this.options = options;
    }
    ingestFrame(frame, maxSamplesOrOptions = this.options.sampling ?? 50_000) {
        this.warnings.length = 0;
        if (this.options.input.cameraProfile) {
            const profile = cameraProfileRegistry.get(this.options.input.cameraProfile);
            if (!profile) {
                this.warnings.push(`Unsupported camera profile: ${this.options.input.cameraProfile}.`);
            }
            else if (profile.transformStatus === 'metadata-only') {
                this.warnings.push(`${profile.name}: ${profile.warning} Transform status: metadata-only.`);
            }
        }
        if (!getColorSpace(this.options.input.inputColorSpace)) {
            this.warnings.push(`Unsupported input color space: ${this.options.input.inputColorSpace}. Falling back to sRGB.`);
        }
        return {
            samples: sampleImageData(frame, maxSamplesOrOptions),
            warnings: [...this.warnings],
        };
    }
    convertFrameToWorkingSpace(samples) {
        const from = getColorSpace(this.options.input.inputColorSpace) ? this.options.input.inputColorSpace : 'sRGB';
        const to = this.options.working.workingSpace;
        const transform = createColorTransform({
            from,
            to,
            inputTransferFunction: this.options.input.inputTransferFunction,
        });
        return {
            samples: samples.map((sample) => ({
                ...sample,
                color: transform.rgb(sample.color),
            })),
            warnings: [...this.warnings, ...transform.warnings],
        };
    }
    convertFrameToOutputSpace(samples) {
        const output = this.options.output?.outputColorSpace ?? this.options.working.workingSpace;
        const to = getColorSpace(output) ? output : 'sRGB';
        if (!getColorSpace(output)) {
            this.warnings.push(`Unsupported output color space: ${output}. Falling back to sRGB.`);
        }
        const transform = createColorTransform({
            from: this.options.working.workingSpace,
            to,
            outputTransferFunction: this.options.output?.outputTransferFunction,
        });
        return {
            samples: samples.map((sample) => ({
                ...sample,
                color: transform.rgb(sample.color),
            })),
            warnings: [...this.warnings, ...transform.warnings],
        };
    }
}
export { D50, D60, D65, CAMERA_PROFILE_WARNING };
//# sourceMappingURL=index.js.map